import React, { useState, useEffect } from 'react';

/**
 * DETECTIVE'S QUIZ modal (Whodunnit) — sealed questions asked privately via a 'quiz'
 * interaction request. Shown ONE QUESTION AT A TIME with a Next button (user, 07-08);
 * the ✕ lets a player bow out (recorded as "never answered the detective").
 *   q1: case-file check (innocents were briefed; the killer gambles)
 *   q2: testimony recall ("who claimed: '<quote>'?")
 * Answers stay sealed until the endgame answer-sheet reveal. Submits
 * { q1: <1-based idx>, q2: <1-based idx> } via respondToInteraction.
 */
const DetectiveQuizModal = ({ quizData, expiresAt, onSubmit, onClose, theme = {} }) => {
  const [answers, setAnswers] = useState({}); // { q1: idx0, q2: idx0, q3: name }
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [timeLeft, setTimeLeft] = useState(() =>
    expiresAt ? Math.max(0, Math.round((new Date(expiresAt) - Date.now()) / 1000)) : 60);

  useEffect(() => {
    const t = setInterval(() => {
      const left = expiresAt ? Math.max(0, Math.round((new Date(expiresAt) - Date.now()) / 1000)) : 0;
      setTimeLeft(left);
      if (expiresAt && left <= 0) onClose?.();
    }, 1000);
    return () => clearInterval(t);
  }, [expiresAt, onClose]);

  if (!quizData) return null;
  const accent = '#b71c1c';
  const questions = [];
  if (quizData.q1) questions.push({ key: 'q1', label: 'THE CASE FILE', q: quizData.q1.q, options: quizData.q1.options });
  if (quizData.q2) questions.push({ key: 'q2', label: 'THE RECORD', q: `${quizData.q2.q}`, quote: quizData.q2.snippet, options: quizData.q2.options });
  if (quizData.q3) questions.push({ key: 'q3', label: 'OFF THE RECORD', q: quizData.q3.q, options: quizData.q3.options });
  if (!questions.length) return null;

  const current = questions[Math.min(step, questions.length - 1)];
  const isLast = step >= questions.length - 1;
  const currentAnswered = answers[current.key] !== undefined;
  const fmtTime = (s) => (s > 90 ? `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}` : `${s}s`);

  const handleSubmit = async () => {
    if (!currentAnswered || submitting) return;
    setSubmitting(true);
    try {
      await onSubmit({
        ...(quizData.q1 && answers.q1 !== undefined ? { q1: answers.q1 + 1 } : {}),
        ...(quizData.q2 && answers.q2 !== undefined ? { q2: answers.q2 + 1 } : {}),
        ...(quizData.q3 && answers.q3 !== undefined ? { suspect: quizData.q3.options[answers.q3] } : {})
      });
      onClose?.();
    } catch (err) {
      console.error('[DetectiveQuizModal] submit failed:', err);
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9998,
      backgroundColor: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(14px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    }}>
      <div style={{
        position: 'relative', width: '94%', maxWidth: '480px', maxHeight: '88dvh', overflowY: 'auto',
        backgroundColor: theme.bgMedium || '#0d0d12', borderRadius: '20px',
        boxShadow: `0 0 0 1px ${accent}35, 0 0 50px ${accent}20, 0 30px 80px rgba(0,0,0,0.8)`,
        padding: '26px 26px 22px'
      }}>
        {/* bow out — recorded as "never answered the detective" */}
        <button
          onClick={() => onClose?.()}
          title="Refuse to answer"
          style={{
            position: 'absolute', top: '12px', right: '14px', width: '30px', height: '30px',
            borderRadius: '50%', border: '1px solid rgba(255,255,255,0.18)',
            backgroundColor: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.65)',
            fontSize: '0.95em', fontWeight: 700, cursor: 'pointer', lineHeight: 1
          }}
        >
          ✕
        </button>

        {/* header */}
        <div style={{ textAlign: 'center', marginBottom: '18px' }}>
          <div style={{
            display: 'inline-block', padding: '4px 14px', borderRadius: '16px',
            backgroundColor: `${accent}20`, border: `1px solid ${accent}45`,
            fontSize: '0.68em', fontWeight: 800, letterSpacing: '2.5px', color: '#ff8c66'
          }}>
            🗂️ PULLED ASIDE · {fmtTime(timeLeft)}
          </div>
          <h2 style={{ color: '#fff', margin: '12px 0 6px', fontSize: '1.5em', fontWeight: 900, letterSpacing: '-0.01em' }}>
            THE DETECTIVE'S QUESTIONS
          </h2>
          <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.82em', fontStyle: 'italic' }}>
            Just you and the detective. Answers stay sealed until the case closes.
          </div>
        </div>

        {/* progress dots */}
        {questions.length > 1 && (
          <div style={{ display: 'flex', gap: '6px', justifyContent: 'center', marginBottom: '14px' }}>
            {questions.map((_, i) => (
              <span key={i} style={{
                width: '7px', height: '7px', borderRadius: '50%',
                backgroundColor: i <= step ? accent : 'rgba(255,255,255,0.15)',
              }} />
            ))}
          </div>
        )}

        {/* current question only */}
        <div key={current.key} style={{ marginBottom: '16px' }}>
          <div style={{ color: '#ff8c66', fontSize: '0.68em', fontWeight: 800, letterSpacing: '1.5px', marginBottom: '5px' }}>
            {current.label} · QUESTION {step + 1} OF {questions.length}
          </div>
          <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.92em', fontWeight: 600, marginBottom: current.quote ? '4px' : '8px' }}>
            {current.q}
          </div>
          {current.quote && (
            <div style={{
              color: 'rgba(255,255,255,0.75)', fontSize: '0.88em', fontStyle: 'italic',
              borderLeft: `3px solid ${accent}70`, padding: '4px 10px', margin: '0 0 8px'
            }}>
              “{current.quote}”
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {current.options.map((opt, i) => {
              const selected = answers[current.key] === i;
              return (
                <button
                  key={i}
                  onClick={() => setAnswers(prev => ({ ...prev, [current.key]: i }))}
                  disabled={submitting}
                  style={{
                    textAlign: 'left', padding: '9px 12px', borderRadius: '10px',
                    border: selected ? `1.5px solid ${accent}` : '1px solid rgba(255,255,255,0.1)',
                    backgroundColor: selected ? `${accent}22` : 'rgba(255,255,255,0.04)',
                    color: selected ? '#fff' : 'rgba(255,255,255,0.75)',
                    fontSize: '0.86em', cursor: 'pointer', transition: 'all 0.15s ease'
                  }}
                >
                  {selected ? '◉ ' : '○ '}{opt}
                </button>
              );
            })}
          </div>
        </div>

        {/* advance / sign */}
        <button
          onClick={() => (isLast ? handleSubmit() : setStep(s => s + 1))}
          disabled={!currentAnswered || submitting}
          style={{
            width: '100%', padding: '12px', borderRadius: '12px', border: 'none',
            background: currentAnswered ? `linear-gradient(135deg, ${accent}, #7f1010)` : 'rgba(255,255,255,0.08)',
            color: currentAnswered ? '#fff' : 'rgba(255,255,255,0.35)',
            fontWeight: 800, fontSize: '0.95em', letterSpacing: '0.5px',
            cursor: currentAnswered && !submitting ? 'pointer' : 'default',
            boxShadow: currentAnswered ? `0 0 24px ${accent}40` : 'none',
            transition: 'all 0.2s ease'
          }}
        >
          {submitting ? 'Filing your statement…'
            : !currentAnswered ? 'Pick an answer'
            : isLast ? '🖋️ SIGN THE STATEMENT'
            : 'Next →'}
        </button>
      </div>
    </div>
  );
};

export default DetectiveQuizModal;
